import { MenuItem, TextField } from '@mui/material';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';
import { useFilter } from '@synergy/components';

export interface PaloQuickFilter {
    /** The query key it writes, the same one the filter dialog uses. */
    key: string;
    /** Fixed choices; without them the filter is a text field. */
    options?: { value: string; label: ReactNode }[];
    /** What stands in the empty field, where the heading alone is not enough. */
    placeholder?: string;
    /**
     * For a list that keeps its query itself instead of standing in a filter
     * context — the master data lists read and write the address directly.
     * Handed in together, they take the place of the context.
     */
    value?: string;
    onChange?: (value: string) => void;
}

const EMPTY = '';

/**
 * One column's filter, sitting under its heading (#2209).
 *
 * It writes the same query key as the dialog beside it, so a chip appears for
 * it, a saved view carries it and the list remembers it like any other filter.
 * Typing waits a moment before it asks the server: a filter that reloads on
 * every keystroke is unusable on a list of 72.000 rows.
 */
export const PaloQuickFilterCell = ({ filter }: { filter: PaloQuickFilter }): ReactElement => {
    const { filter: params, updateFilter } = useFilter();
    const controlled = undefined !== filter.onChange;
    const applied = (controlled ? filter.value : params.get(filter.key)) ?? EMPTY;
    const [draft, setDraft] = useState(applied);

    // Someone else may change the query: a chip removed, a view applied, the
    // state restored on open.
    useEffect(() => setDraft(applied), [applied]);

    const apply = (value: string): void => {
        if (value === applied) {
            return;
        }

        if (controlled) {
            filter.onChange?.(value);

            return;
        }

        const next = new URLSearchParams();

        Array.from(params.entries())
            .filter(([key]) => key !== filter.key)
            .forEach(([key, entry]) => next.append(key, entry));

        if (value) {
            next.append(filter.key, value);
        }

        updateFilter(next);
    };

    useEffect(() => {
        if (filter.options || draft === applied) {
            return;
        }

        const timer = setTimeout(() => apply(draft), 400);

        return () => clearTimeout(timer);
        // apply reads the current query; listing it would re-arm the timer on
        // every render of the list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft]);

    return (
        <TextField
            select={!!filter.options}
            size="small"
            fullWidth={true}
            value={draft}
            placeholder={filter.placeholder}
            onChange={event => {
                setDraft(event.target.value);

                if (filter.options) {
                    apply(event.target.value);
                }
            }}
            onKeyDown={event => {
                if ('Enter' === event.key) {
                    apply(draft);
                }
            }}
            SelectProps={{ displayEmpty: true }}
            sx={{
                '& .MuiInputBase-root': {
                    // The text field came out 25 pixels high beside choices of 29.
                    height: '29px',
                    fontSize: '12px',
                    backgroundColor: tokens.color.surface,
                },
                '& .MuiInputBase-input': { padding: '4px 7px' },
                '& .MuiSelect-select': { padding: '4px 7px' },
            }}
        >
            {filter.options && (
                <MenuItem value={EMPTY} sx={{ fontSize: '12px', color: tokens.color.ink3 }}>
                    {filter.placeholder ?? 'Alle'}
                </MenuItem>
            )}
            {filter.options?.map(option => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '12px' }}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );
};
